import { toTypedSchema } from '@vee-validate/zod';
import { z } from '@pkg/form-builder/zod';
import { useForm } from 'vee-validate';
import { logger } from '@pkg/logger';
import { countryFromLocale } from '@pkg/utilities/i18n';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { allCountries } from '@pkg/config/misc/countries';
import { markets } from '@pkg/config/languages';
import { singletonDocIdByName } from '@pkg/sanity-utils/singletonDocId';
import { SINGLETON_DOCUMENT } from '@pkg/config/sanity/singletons';
import {
  BUSINESS_AUDIENCE_FILTER,
  SITE_IDS,
  WWW_AUDIENCE_FILTER,
} from '@pkg/config/sanity/audience';
import { findSolutionPage, type QueryResponse, type SolutionPageMatch } from './findSolutionPage';

/**
 * @todo(jamie@hexdigital.com): The solution finder code needs visual documentation
 *                              for the logic and flow, drawn up in Figma.
 *                              It also needs integrating with Google Maps Platform.
 */

function solutionFinderFormShape() {
  // @todo(jamie@hexdigital.com): Localise messages
  return z.object({
    vendorTypeId: z.string({
      required_error: 'Please select a type for your business',
    }).min(1),
    countryIso: z.string({
      required_error: 'Please select a country for your business.',
    }).min(1),
    postcode: z.string({
      required_error: 'Please enter a postcode for your business.',
    }).min(1),
  });
}

/**
 * Initialise the solution finder form, as well as handle its submission by redirecting to a relevant page.
 *
 * We default the country selection to whatever locale the use is on, to try and save them some clicks.
 */
export function useSolutionFinder() {
  const { locale } = useI18n();

  const initialCountry = countryFromLocale(unref(locale) as LOCALE_CODE);
  const showCountrySelect = ref(!initialCountry);
  const countries = allCountries.map(country => ({
    title: country.title,
    value: country.countryIso,
  }));

  const formSchema = toTypedSchema(solutionFinderFormShape());
  const form = useForm({
    validationSchema: formSchema,
    initialValues: {
      countryIso: initialCountry?.countryIso,
      postcode: 'inactive',
    },
  });
  const submitHandler = submitHandlerFn();
  const onSubmit = form.handleSubmit(submitHandler);

  return {
    formSchema,
    form,
    onSubmit,
    initialCountry,
    showCountrySelect,
    countries,
  };
}

function submitHandlerFn() {
  const { locale } = useI18n();
  const router = useRouter();
  const localePath = useLocalePath();
  const { public: runtimePublic } = useRuntimeConfig();

  /** Business uses `/api/sanity-fetch`; web keeps `/api/sanity-secure`. */
  const sanityFetchPath
    = (runtimePublic as { sanityFetchPath?: string }).sanityFetchPath ?? '/api/sanity-secure';

  return async function submitHandler(values: { vendorTypeId: string, countryIso: string, postcode: string }) {
    try {
      logger.debug('[Solution Finder] Submitting form with values', values);

      // If country is one of our markets
      // Otherwise just send country to get marketplace page

      let data = {
        vendorTypeId: values.vendorTypeId,
        postcode: values.postcode,
        country: '',
      };

      if (markets[values.countryIso]) {
        // Request data from Google's API and merge it in here, changing the key case to camelCase
        data = {
          ...data,
          country: markets[values.countryIso].country,
        };

        // const exampleDataAtThisPoint = {
        //   vendorTypeId: '1a2gh3s4j5f'',
        //   // Example response from Google reverse geolocation API
        //   postal_town: 'Coulsdon',
        //   administrative_area_level_2: 'Greater London',
        //   administrative_area_level_1: 'England',
        //   country: 'United Kingdom',
        //   postcode: 'CR5 2JE',
        // };
      } else {
        data = {
          ...data,
          country: allCountries.find(country => country.countryIso === values.countryIso)?.title ?? '',
        };
      }

      // Query CMS data for matching marketplace page

      const siteId = (runtimePublic as { siteId?: string }).siteId === SITE_IDS.BUSINESS
        ? SITE_IDS.BUSINESS
        : SITE_IDS.WWW;
      const audienceFilter = siteId === SITE_IDS.BUSINESS
        ? BUSINESS_AUDIENCE_FILTER
        : WWW_AUDIENCE_FILTER;
      const query = findSolutionPage(data, audienceFilter);
      const params = {
        fallbackPageId: singletonDocIdByName(SINGLETON_DOCUMENT.SOLUTION_FINDER_FALLBACK, unref(locale)),
        locale: unref(locale),
      };
      logger.debug('[Solution Finder] Query', query, params);

      const response = await $fetch<QueryResponse>(sanityFetchPath, {
        method: 'POST',
        body: { query, params },
      });
      logger.debug('[Solution Finder] Response: ', response);

      const slug = resolveServableSlug(response.pages, response.replacements)
        ?? resolveServableSlug(response.backupPages, response.replacements);

      if (slug) {
        router.push(localePath(slug));
        return;
      }

      if (response.fallbackPage && response.fallbackPage.slug) {
        router.push(localePath(response.fallbackPage.slug));
        return;
      }

      // Maybe go to a generic page if possible?
      logger.warn('Couldn\'t find a relevant marketplace page. This should be looked into - likely missing content in CMS');
    } catch (err) {
      logger.error(err);
      // Do something if there's a problem? Maybe redirect to a generic marketplace page? It's important we don't lose this lead, so we need to give them something.
    }
  };
}

function resolveServableSlug(
  pages: SolutionPageMatch[],
  replacements: QueryResponse['replacements'],
) {
  if (!pages.length) {
    return undefined;
  }

  const winner = getPage(pages);
  const candidates = winner ? [winner, ...pages.filter(page => page !== winner)] : pages;

  for (const page of candidates) {
    const replacement = replacements.find(item => item.leafSlug === page.leafSlug);
    if (replacement?.slug) {
      return replacement.slug;
    }
    if (page.servable && page.slug) {
      return page.slug;
    }
  }

  return undefined;
}

function getPage(pages: QueryResponse['pages']) {
  if (pages.length === 1) {
    return pages[0];
  }

  const localities = pages.filter(page => page.localities && page.localities > 0);

  if (localities.length) {
    return localities[0];
  }

  const areas = pages.filter(page => page.areas && page.areas > 0);

  if (areas.length) {
    return areas[0];
  }

  return pages[0];
}
